/** @jsxImportSource ./simboluri-jsx */
// VEDEREA 2D a Studioului („să fie și un buton de comutat
// între 2D și 3D; când comutăm în 2D să vedem practic informațiile de la
// pasul 4"). Aceeași întrebare ca 3D-ul — „ce s-a construit" — pusă în
// proiecție de plan: desenul nivelului, cu propunerea Pasului 4 peste el.
//
// Aici rămâne DOAR desenul + banda de niveluri: comutatorul de proiecție și
// cifrele Pasului 4 stau în bara unică din stânga-jos, pe care o randează
// Studio.tsx pentru ambele proiecții.
//
// FUNDAȚIA e o VEDERE a planului, nu un tab și nu un strat al subsolului
// („fundația trebuie separată de subsol, să fie butoane
// separate; acum dai click pe subsol și vezi fundația"). Deci: buton propriu în
// banda de niveluri, sub ele, care cere ACELAȘI plan al nivelului de jos cu
// layerele fundației APRINSE (`?fundatie=1`, v. `app._stampa_fundatia`) — banda
// tălpii și axele denumite ale Pasului 6, peste stâlpii și pereții pe care le
// duc. Click pe un ax ⇒ drawer-ul cu verificările LUI.
//
// Vederea se deschide la PARTER, nu la primul nivel din listă: parterul e
// nivelul despre care vorbește lumea, subsolul e o excepție.

import { fundatiaAdoptata } from "./fundatia";
import { useT } from "../i18n";
import { useState } from "react";
import { DrawingView } from "./DwgViewer";
import DrawerAxFundatie from "./DrawerAxFundatie";
import { StepState } from "./tipuri";
import { stepDrawingUrl } from "./apiStructura";
import { indexParter } from "./elemente";
import { hashSectiune } from "./legaturi";

export default function StudioPlan({
  id,
  cloneId,
  solutie,
  steps,
  vedereInitiala,
  vedere: vedereAleasa,
  laVedere,
  straturiIn,
}: {
  id: string;
  cloneId: string | null;
  solutie?: string | null;
  steps: Record<string, StepState>;
  // ruta veche `?tab=fundatie` (cardul capitolului 4) deschide direct vederea
  // de fundație, nu parterul
  vedereInitiala?: "fundatie";
  // VEDEREA ALEASĂ DIN AFARĂ: Studioul o ține în meniul lui din stânga, ca
  // lista de cote a arhitecturii. Cu ea dată, banda din dreapta-jos nu se mai
  // desenează — ar fi a doua alegere pentru același lucru.
  vedere?: number | "fundatie";
  laVedere?: (v: number | "fundatie") => void;
  // grupul «Afișare» al meniului Studioului, unde se scriu straturile desenului
  straturiIn?: HTMLElement | null;
}) {
  const t = useT();
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const r = steps["model_structura"]?.result as any;
  const rf = fundatiaAdoptata(steps) as any;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const etaje: { nivel: string; nr_stalpi?: number; nr_grinzi?: number }[] =
    r?.etaje ?? [];
  // vederea: indexul nivelului, sau „fundatie" — o vedere a nivelului de jos,
  // cu layerele tălpii aprinse
  const [vedereLocala, setVedereLocala] = useState<number | "fundatie">(() =>
    vedereInitiala === "fundatie" ? "fundatie" : indexParter(etaje));
  const vedere = vedereAleasa ?? vedereLocala;
  const setVedere = laVedere ?? setVedereLocala;
  // axul de fundație selectat în desen („Ax 4")
  const [ax, setAx] = useState<string | null>(null);
  const areFundatie = !!rf?.latime_m;

  if (!etaje.length) {
    return (
      <div className="ob-studio-gol">
        <div>
          <p>{t("struct.plan.gol")}</p>
          <a href={hashSectiune(id, "pipeline", solutie ?? null)}>pipeline →</a>
        </div>
      </div>
    );
  }

  const eFundatie = vedere === "fundatie";
  const nivel = eFundatie ? 0 : Math.min(vedere as number, etaje.length - 1);
  const et = etaje[nivel];

  return (
    // planul și drawer-ul lui sunt FRAȚI într-un rând: altfel drawer-ul ar fi
    // rămas în interiorul canvasului, sub desen
    <div className="ob-plan-wrap">
      <div className="ob-plan-canvas">
        <div className="ob-studio-plan">
        <DrawingView
          key={`${id}:${cloneId ?? ""}:${nivel}:${eFundatie ? "f" : "n"}`}
          src={stepDrawingUrl(id, "model_structura", nivel, cloneId,
                              eFundatie ? { fundatie: "1" } : undefined)}
          versiune={steps["model_structura"]?.updated_at}
          title={eFundatie
            ? t("struct.plan.fundatie", { nivel: et.nivel })
            : t("struct.plan.model", { nivel: et.nivel })}
          embed
          // axele de fundație poartă numele din tabel („Ax 4"), deci selecția
          // din desen e direct referința elementului
          focusId={ax}
          onSelect={(idEnt, e) => {
            const tip = e && e.t !== "n" ? e.et : null;
            if (idEnt && (tip === "FOUNDATION_AXIS" || tip === "AXIS_ID"))
              setAx(idEnt);
          }}
          linkEticheta={t("struct.plan.link_ax")}
          straturiIn={straturiIn}
        />
        </div>

      {/* nivelurile, în dreapta-jos; fundația e ULTIMA, sub ele */}
      {!laVedere && (etaje.length > 1 || areFundatie) && (
        <div className="ob-card ob-studio-plutitor ob-plan-nivele">
          {etaje.map((e, i) => (
            <button
              key={i}
              className={`ob-nivel${!eFundatie && i === nivel ? " on" : ""}`}
              onClick={() => setVedere(i)}
              title={t("struct.plan.nivel_titlu", {
                stalpi: e.nr_stalpi ?? "—",
                grinzi: e.nr_grinzi ?? "—",
              })}
            >
              {e.nivel}
            </button>
          ))}
          {areFundatie && (
            <button
              className={`ob-nivel${eFundatie ? " on" : ""}`}
              onClick={() => setVedere("fundatie")}
              title={t("struct.plan.fundatie_titlu", {
                B: rf.latime_m,
                D: rf.adancime_m,
              })}
            >
              {t("struct.plan.fundatie_buton")}
            </button>
          )}
        </div>
      )}

      </div>

      {/* Drawer-ul cu regulile de amplasare a fost SCOS:
          verificările au un singur loc — pagina Verificări și pagina
          elementului. Contorul lor e un chip în bara din stânga-jos.

          Drawer-ul de AX DE FUNDAȚIE apare doar la click pe un ax din layerul
          fundației: e verificarea unui element, cerută explicit. */}
      {ax && (
        <DrawerAxFundatie
          id={id}
          solutie={solutie}
          steps={steps}
          ales={ax}
          onAles={setAx}
        />
      )}
    </div>
  );
}
